import React, { Component } from "react";
import PropTypes from "prop-types";
import CheckBox from "upn-component-check-box";
import TextInput from "upn-component-text-input";
import Label from "upn-component-label";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";
import isNumber from "lodash/isNumber";
import { css } from "emotion";

const UPN_BLUE = "rgb(0, 113, 206)";
const UPN_YELLOW = "rgb(255, 215, 0)";

const rowStyles = css`
  padding: 2px 4px;
  font-size: 0.85em;
  & td {
    padding: 2px 2px 2px 4px;
    border-right: 1px solid #b3b3b3;
  }
`;

const bodyRowStyles = css`
  background-color: #fff;
  &:nth-child(2n) {
    background-color: #f5f5f5;
  }

  &:hover {
    background: #fffff0;
  }

  &.clickable-row:hover {
    cursor: pointer;
    background: #ffffc8;
  }
  ${rowStyles};
`;

const footerRowStyles = css`
  background-color: #e5e5e5;
  font-weight: bold;
  &.clickable-footer:hover {
    cursor: pointer;
  }
  ${rowStyles};
`;

const tableStyles = css`
  border: 1px solid #b3b3b3;
  border-collapse: collapse;
  text-align: left;
  width: 100%;

  & thead th {
    background-color: ${UPN_BLUE};
    color: white;
    font-size: 0.73em;
    padding: 2px 2px 2px 4px;
    border-bottom: 1px solid #b3b3b3;
    border-right: 1px solid #ccc;
  }

  & thead th.sorted {
    color: ${UPN_YELLOW};
  }

  & thead th.sorted-asc::after {
    content: "\\25B4"; // ▴
  }

  & thead th.sorted-desc::after {
    content: "\\25BE"; // ▾
  }

  & tbody td.checkbox-row-end {
    border-bottom: 1px solid #b3b3b3;
  }

  & tbody td.accordion {
    border-top: 1px solid #b3b3b3;
    border-bottom: 1px solid #b3b3b3;
  }
`;

const filterWrapperStyles = css`
  & .filter-box {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-bottom: 0.5em;
  }
  & .search-box {
    justify-self: start;
  }
  & .filter-text {
    margin-right: 1em;
  }
  & .results-text {
    justify-self: end;
  }
`;

const labelStyles = css`
  display: block;
`;

const checkBoxStyles = css`
  float: right;
`;

//If a value isn't a number, attempts to parse the value to a float; otherwise returns 0 as a float.
function getNumber(val) {
  let output = val;
  if (!isNumber(output)) output = parseFloat(val);
  if (isNaN(output)) output = 0.0;
  return output;
}

//Returns an object representing a footer where the keys are those specified and the values are the summed columns of the data.
export function sum(data, keys) {
  if (data && keys) {
    if (isArray(data) && data[0] && isObject(data[0]) && isArray(keys)) {
      return keys.reduce(
        (summedData, key) => ({
          ...summedData,
          [key]: data.reduce(
            (sum, row) => (sum += row[key] ? getNumber(row[key]) : 0),
            0
          ),
        }),
        {}
      );
    }
  }
  throw new Error(
    "sum requires the parameters data (of type array(object)) and keys of type array."
  );
}

//Returns an object representing a footer where the keys are those specified and the values are the averaged columns of the data.
export function average(data, keys) {
  if (data && keys) {
    if (isArray(data) && data[0] && isObject(data[0]) && isArray(keys)) {
      return keys.reduce(
        (averagedData, key) => ({
          ...averagedData,
          [key]: data.reduce(
            (average, row, i) =>
              (average * i + (row[key] ? getNumber(row[key]) : 0)) / (i + 1),
            0
          ),
        }),
        {}
      );
    }
  }
  throw new Error(
    "average requires the parameters data (of type array(object)) and keys of type array."
  );
}

export default class DataTable extends Component {
  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.object), //Data of the body
    dataKey: PropTypes.string, // key to uniquely identify a row
    footer: PropTypes.object, //Data of the footer
    cols: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
      })
    ),
    accordions: PropTypes.objectOf(PropTypes.any), // specify contents of accordion for individual rows (by dataKey)
    className: PropTypes.string,
    rowClassNames: PropTypes.objectOf(
      PropTypes.oneOf([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
    ),
    checkBoxColumn: PropTypes.bool,
    comparators: PropTypes.objectOf(PropTypes.func),
    defaultFormatter: PropTypes.func, //Formatter applied to all data (including footer) without a specific formatter.
    footerFormatters: PropTypes.objectOf(PropTypes.func), //Formatter applied to footer row beneath the table, each column is supplied with that columns key and data.
    formatters: PropTypes.objectOf(PropTypes.func), //Formats data column-wise depending upon the key, accepts val and row as parameters.
    filterable: PropTypes.bool,
    filterLabel: PropTypes.string,
    filter: PropTypes.string,
    onChangeFilter: PropTypes.func,
    onCellClick: PropTypes.func,
    onCheck: PropTypes.func, //Gets passed the row data.
    onCheckAll: PropTypes.func,
    onFooterClick: PropTypes.func,
    onHeaderClick: PropTypes.func,
    onRowClick: PropTypes.func,
    sortable: PropTypes.bool,
    defaultSortBy: PropTypes.string,
    defaultSortOrder: PropTypes.oneOf(["asc", "desc"]),
    sortBy: PropTypes.string,
    sortOrder: PropTypes.oneOf(["asc", "desc"]),
  };

  static defaultProps = {
    checkBoxColumn: false,
    defaultFormatter: (val) => (val == null ? "" : val.toString()),
    filterLabel: "Filter results:",
    defaultSortOrder: "asc",
  };

  state = {
    filter: "",
    sortBy: this.props.defaultSortBy,
    sortOrder: this.props.defaultSortOrder,
  };

  defaultComparator = (a, b) => (a > b ? 1 : a < b ? -1 : 0);

  areAllRowsChecked = () =>
    this.props.data.reduce((acc, { selected }) => {
      if (acc === false) {
        return false;
      } else {
        return selected;
      }
    }, true);

  checkAll = () =>
    this.props.onCheckAll && this.props.onCheckAll(!this.areAllRowsChecked());

  assignColumnNames = () => {
    let columns = this.props.cols;
    if (!columns && this.props.data && this.props.data[0]) {
      //generate cols if none given (and theres data)
      columns = Object.keys(this.props.data[0]).map((key) => {
        return { key: key, label: key };
      });
    }
    return columns;
  };

  onHeaderClick = (col) => {
    if (this.props.onHeaderClick) {
      this.props.onHeaderClick(col);
    }
    if (this.props.sortable === true) {
      this.setState((prevState) => {
        let newOrder = "asc",
          newKey = prevState.sortBy;
        if (prevState.sortBy === col) {
          if (prevState.sortOrder === "asc") {
            newOrder = "desc";
          }
        } else {
          newKey = col;
        }
        return {
          sortBy: newKey,
          sortOrder: newOrder,
        };
      });
    }
  };

  formatItem = (key, val, row) => {
    if (this.props.formatters && this.props.formatters[key]) {
      return this.props.formatters[key](val, row);
    } else {
      return this.props.defaultFormatter(val, row);
    }
  };

  onFilterChange = (string, isValid) => {
    if (!this.props.filter) {
      this.setState({ filter: string, isFilterValid: isValid });
    }
    if (this.props.onChangeFilter) {
      this.props.onChangeFilter(string, isValid);
    }
  };

  filterItems = (data, filter) =>
    data.filter((row) =>
      Object.keys(row).reduce((acc, key) => {
        if (acc === true) {
          return true;
        }
        let itemString = this.formatItem(key, row[key], row);
        itemString = itemString.toString().toLocaleLowerCase();
        if (itemString.includes(filter.toLocaleLowerCase())) {
          return true;
        } else {
          return false;
        }
      }, false)
    );

  sortedRows = () => {
    let sortBy = this.props.sortable
      ? this.props.sortBy || this.state.sortBy
      : undefined;
    let sortOrder = this.props.sortOrder || this.state.sortOrder;
    if (this.props.sortable && sortBy != null) {
      return this.props.data
        ? this.props.data.sort((a, b) => {
            let rtn = 0;
            let comparator = this.defaultComparator;
            if (this.props.comparators && this.props.comparators[sortBy]) {
              comparator = this.props.comparators[sortBy];
            }
            rtn = comparator(a[sortBy], b[sortBy]);
            if (sortOrder !== "asc") {
              rtn = rtn * -1;
            }
            return rtn;
          })
        : undefined;
    }
    return this.props.data || undefined;
  };

  getHeaderGroupCell = ({ key, label, colSpan }) => {
    return (
      <th key={key} onClick={() => this.onHeaderClick(key)} colSpan={colSpan}>
        {label}
      </th>
    );
  };

  getHeaderCell = ({ key, label }) => {
    let classNames = [];
    let sortBy = this.props.sortable
      ? this.props.sortBy || this.state.sortBy
      : undefined;
    let sortOrder = this.props.sortOrder || this.state.sortOrder;
    if (sortBy != null && key === sortBy) {
      classNames.push("sorted");
      classNames.push(`sorted-${sortOrder}`);
    }
    let cell = (
      <th
        className={classNames.join(" ")}
        key={key}
        onClick={() => this.onHeaderClick(key)}
      >
        {label == null ? key : label}
      </th>
    );
    if (this.props.checkBoxColumn && key === "selected") {
      cell = (
        <th className="checkbox-col-header" key={key} onClick={this.checkAll}>
          {label !== "selected" && label}
          <CheckBox
            className={checkBoxStyles}
            checked={this.areAllRowsChecked()}
          />
        </th>
      );
    }
    return cell;
  };

  getTableHeader = (cols) => {
    let [groupCells, cells] = cols.reduce(
      ([groupCells, cells], col) => {
        let newGroupCell;
        let newCells;
        // if is a grouped col (has sub cols)
        if (col.cols && col.cols.length > 0) {
          newGroupCell = this.getHeaderGroupCell({
            key: col.key,
            label: col.label,
            colSpan: col.cols.length,
          });
          newCells = col.cols.map(this.getHeaderCell);
        } else {
          newGroupCell = this.getHeaderGroupCell({
            key: `${col.key}-group`,
            label: "",
          });
          newCells = [this.getHeaderCell(col)];
        }
        return [
          [...groupCells, newGroupCell],
          [...cells, ...newCells],
        ];
      },
      [[], []]
    );
    let hasGroups = !!cols.find((col) => col.cols && col.cols.length > 0);
    return (
      <thead>
        {hasGroups && <tr>{groupCells}</tr>}
        <tr>{cells}</tr>
      </thead>
    );
  };

  getFlattenedCols(cols) {
    cols = cols.reduce((arr, col) => {
      return [...arr, ...(col.cols ? col.cols : [col])];
    }, []);
    return cols;
  }

  getTableRows = (cols, data) => {
    // flatten column data to exclude groupings
    cols = this.getFlattenedCols(cols);
    let sharedRowClassNames = [bodyRowStyles];
    if (this.props.onRowClick) {
      sharedRowClassNames.push("clickable-row");
    }
    return data.map((row, i) => {
      let checkBox;
      if (
        this.props.checkBoxColumn &&
        cols.find(({ key }) => key === "selected")
      ) {
        if (this.props.formatters && this.props.formatters["selected"]) {
          checkBox = this.props.formatters["selected"](row.selected, row, i);
        } else {
          checkBox = (
            <CheckBox className={checkBoxStyles} checked={row["selected"]} />
          );
        }
      }

      let thisRowClassNames = [];
      if (this.props.dataKey && this.props.rowClassNames) {
        let rowKey = row[this.props.dataKey];
        thisRowClassNames = this.props.rowClassNames[rowKey] || [];
        if (typeof thisRowClassNames === "string") {
          thisRowClassNames = [thisRowClassNames];
        }
      }

      let DataRow = (
        <tr
          className={[...sharedRowClassNames, ...thisRowClassNames].join(" ")}
          key={i}
          onClick={() =>
            this.props.onRowClick ? this.props.onRowClick(row) : null
          }
        >
          {cols.map((col) => {
            let key = col.key;
            let val = row[key];
            if (checkBox && key === "selected") {
              return (
                <td
                  className={
                    i === this.props.data.length - 1 && "checkbox-row-end"
                  }
                  key={key}
                  onClick={
                    this.props.onCheck &&
                    (() =>
                      this.props.onCheck(i, {
                        ...row,
                        selected: !row["selected"],
                      }))
                  }
                >
                  {checkBox}
                </td>
              );
            } else if (key !== "selected") {
              if (this.props.formatters && this.props.formatters[key]) {
                val = this.props.formatters[key](val, row);
              } else {
                val = this.props.defaultFormatter(val, row);
              }
              return (
                <td
                  key={key}
                  onClick={() =>
                    this.props.onCellClick
                      ? this.props.onCellClick(key, row)
                      : null
                  }
                >
                  {val}
                </td>
              );
            }
          })}
        </tr>
      );
      if (
        this.props.accordions &&
        this.props.accordions[row[this.props.dataKey]]
      ) {
        let AccordionRow = (
          <tr>
            <td colSpan={cols.length} className="accordion">
              {this.props.accordions[row[this.props.dataKey]]}
            </td>
          </tr>
        );
        return [DataRow, AccordionRow];
      }
      return DataRow;
    });
  };

  getFooterRow = (cols) => {
    cols = this.getFlattenedCols(cols);
    let footer = this.props.footer;
    let rowClassNames = [footerRowStyles];
    if (this.props.onFooterClick) {
      rowClassNames.push("clickable-footer");
    }
    let footerData;
    if (footer) {
      footerData = cols.map(({ key }) => ({ key: key, val: footer[key] }));
      return (
        <tr className={rowClassNames.join(" ")}>
          {footerData.map(({ key, val }) => {
            let columnData = this.props.data.map((row) => row[key]);
            if (key !== "selected") {
              return (
                <td
                  key={key}
                  onClick={() =>
                    this.props.onFooterClick
                      ? this.props.onFooterClick(key, val, columnData)
                      : null
                  }
                >
                  {this.props.footerFormatters &&
                  this.props.footerFormatters[key]
                    ? this.props.footerFormatters[key](val, columnData)
                    : this.props.defaultFormatter(val)}
                </td>
              );
            }
          })}
        </tr>
      );
    } else return null;
  };

  getFullTable = ({ classNames, cols, data }) => {
    classNames = classNames || [];
    classNames.push(tableStyles);
    return (
      <table className={classNames.length > 0 && classNames.join(" ")}>
        {this.getTableHeader(cols)}
        <tbody>{this.getTableRows(cols, data)}</tbody>
        <tfoot>{this.getFooterRow(cols)}</tfoot>
      </table>
    );
  };

  getFilterableDataTable = ({ classNames, cols, data }) => {
    classNames = classNames || [];
    let filter =
      this.props.filter != null ? this.props.filter : this.state.filter;
    let Table = () => this.getFullTable({ cols: cols, data: data });
    classNames.push(filterWrapperStyles);
    return (
      <div className={classNames.join(" ")}>
        <div className="filter-box">
          <span className="search-box">
            <label className="filter-text">{this.props.filterLabel}</label>
            <TextInput value={filter} onChange={this.onFilterChange} />
          </span>
          <span className="results-text">
            <label>{`( ${data.length} / ${this.props.data.length} )`}</label>
          </span>
        </div>
        <Table />
      </div>
    );
  };

  render = () => {
    let classNames = ["upn-component-data-table"];
    if (this.props.className) {
      classNames.push(this.props.className);
    }
    let cols = this.assignColumnNames();
    let filter =
      this.props.filter != null ? this.props.filter : this.state.filter;
    let FilterableTable = this.getFilterableDataTable;
    let Table = this.getFullTable;
    if (
      (this.props.data && this.props.data.length > 0) ||
      (this.props.cols && this.props.cols.length > 0)
    ) {
      let data = this.sortedRows() || [];
      if (this.props.filterable && filter.length > 0) {
        data = this.filterItems(data, filter);
      }
      if (this.props.filterable === true) {
        return (
          <FilterableTable classNames={classNames} cols={cols} data={data} />
        );
      } else {
        return <Table classNames={classNames} cols={cols} data={data} />;
      }
    } else {
      classNames.push(labelStyles);
      return (
        <Label className={classNames.join(" ")} theme="info">
          No data
        </Label>
      );
    }
  };
}
